<template>
  <div class="quality-chart">
    <ChartBox :option="option" />
  </div>
</template>

<script setup>
import { computed } from "vue";
import ChartBox from "@/components/xiaofeiTradeData/ChartBox.vue";
import { formatPrice, productAverage, priceProducts } from "./priceData";

const costProducts = [
  priceProducts[1],
  priceProducts[0],
  priceProducts[2],
  priceProducts[3],
  priceProducts[4],
  priceProducts[6],
  priceProducts[9],
];

const products = costProducts.map((product) => product.name.replace("朱里", ""));
const costData = costProducts.map((product) => product.cost);
const avgData = costProducts.map((product) => Number(productAverage(product).toFixed(1)));
const upperData = costProducts.map((product) => product.upper);
const lowerData = costProducts.map((product) => product.lower);

function isSameValue(a, b) {
  return Math.abs(Number(a) - Number(b)) < 0.01;
}

function formatLineLabel(params, compareData) {
  return isSameValue(params.value, compareData[params.dataIndex]) ? "" : params.value;
}

const option = computed(() => ({
  color: ["#1559d8", "#22d9ff", "#a8f7ff", "#8b5cff"],
  grid: { top: 92, left: 70, right: 100, bottom: 58 },
  legend: {
    top: 18,
    right: 24,
    icon: "roundRect",
    itemWidth: 22,
    itemHeight: 10,
    itemGap: 22,
    textStyle: { color: "#d8f4ff", fontSize: 18, fontWeight: "bold" },
    data: ["生产成本", "线上均价", "合理上限", "合理下限"],
  },
  tooltip: {
    trigger: "axis",
    axisPointer: { type: "shadow" },
    textStyle: { fontSize: 20,fontWeight: "bold" },
    formatter: (params) => {
      const title = params?.[0]?.axisValueLabel || "";
      const lines = params.map((item) => `${item.marker}${item.seriesName}：${formatPrice(item.value, 1)}元`);
      return [title, ...lines].join("<br/>");
    },
  },
  xAxis: {
    type: "category",
    data: products,
    axisTick: { show: false },
    axisLine: { lineStyle: { color: "rgba(104, 172, 232, .62)" } },
    axisLabel: {
      color: "#d7efff",
      fontSize: 20,
      fontWeight: "bold",
      interval: 0,
      margin: 16,
    },
  },
  yAxis: {
    type: "value",
    min: 0,
    max: 65,
    interval: 10,
    name: "元/标准件",
    nameTextStyle: { color: "#9fdfff", fontSize: 20,fontWeight: "bold", padding: [0, 0, 8, 0] },
    axisLine: { show: true, lineStyle: { color: "rgba(104, 172, 232, .38)" } },
    axisLabel: { color: "#bde7ff", fontSize: 20,fontWeight: "bold" },
    splitLine: {
      lineStyle: {
        color: "rgba(93, 163, 226, .18)",
        type: "dashed",
      },
    },
  },
  series: [
    {
      name: "生产成本",
      type: "bar",
      barWidth: 24,
      barGap: "100%",
      data: costData,
      label: {
        show: true,
        position: "insideTop",
        distance: 4,
        color: "#bdeeff",
        fontSize: 20,
        fontWeight: "bold",
        formatter: "{c}",
      },
      itemStyle: {
        borderRadius: [4, 4, 0, 0],
        color: {
          type: "linear",
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: "#1f76ef" },
            { offset: 1, color: "rgba(14, 53, 137, 0.42)" },
          ],
        },
      },
    },
    {
      name: "线上均价",
      type: "bar",
      barWidth: 24,
      data: avgData,
      label: {
        show: true,
        position: "top",
        distance: 10,
        color: "#f0fbff",
        fontSize: 22,
        fontWeight: "bold",
        formatter: "{c}",
      },
      itemStyle: {
        borderRadius: [4, 4, 0, 0],
        color: ({ dataIndex }) => ({
          type: "linear",
          x: 0,
          y: 0,
          x2: 0,
          y2: 1,
          colorStops: [
            { offset: 0, color: dataIndex >= 5 ? "#8a6cff" : "#45efff" },
            { offset: 1, color: dataIndex >= 5 ? "#4d37c9" : "#0780e8" },
          ],
        }),
      },
      markPoint: {
        symbol: "circle",
        symbolSize: 15,
        label: {
          show: true,
          position: "right",
          distance: 8,
          color: "#e9fbff",
          fontSize: 20,
          fontWeight: "bold",
          backgroundColor: "rgba(13, 105, 190, 0.78)",
          borderColor: "rgba(58, 207, 255, 0.95)",
          borderWidth: 1,
          borderRadius: 4,
          padding: [4, 8],
        },
        itemStyle: {
          color: "#0056ff",
          shadowBlur: 14,
          shadowColor: "rgba(34, 207, 255, 0.9)",
        },
        data: [
          { name: "高价预警", coord: ["黄姜粉", 39.8], value: "高价偏离" },
          { name: "下限预警", coord: ["红薯干", 16.8], value: "接近下限" },
        ],
      },
    },
    {
      name: "合理上限",
      type: "line",
      smooth: true,
      symbol: "circle",
      symbolSize: 9,
      lineStyle: { width: 4, color: "#a8f7ff" },
      itemStyle: {
        color: "#ecffff",
        borderColor: "#39eaff",
        borderWidth: 2,
      },
      label: {
        show: true,
        position: "top",
        distance: 14,
        color: "#bdf8ff",
        fontSize: 20,
        fontWeight: "bold",
        formatter: (params) => formatLineLabel(params, avgData),
      },
      data: upperData,
    },
    {
      name: "合理下限",
      type: "line",
      smooth: true,
      symbol: "circle",
      symbolSize: 8,
      lineStyle: { width: 3, color: "#8b5cff" },
      itemStyle: {
        color: "#bc9bff",
        borderColor: "#7b5cff",
        borderWidth: 2,
      },
      label: {
        show: true,
        position: "bottom",
        distance: 12,
        color: "#cbbaff",
        fontSize: 20,
        fontWeight: "bold",
        formatter: (params) => formatLineLabel(params, costData),
      },
      data: lowerData,
    },
  ],
}));
</script>

<style lang="less" scoped>
.quality-chart {
  height: 100%;
  position: relative;
}
</style>
